// AdminDashboardPage.js — /admin
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../../firebaseConfig';
import Layout from '../Layout';
import { AdminLoadingBar, AdminLoadingStyles, AdminSpinner } from './AdminLoadingUi';

const card  = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '20px 28px', minWidth: 160 };
const label = { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 };
const big   = { fontSize: 32, fontWeight: 700, color: '#1e293b' };
const row   = { display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 };

async function adminFetch(path) {
  const token = await auth.currentUser.getIdToken();
  const res   = await fetch(path, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export default function AdminDashboardPage() {
  const [stats,  setStats]  = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [error,  setError]  = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const [s, a] = await Promise.all([
          adminFetch('/api/admin/stats'),
          adminFetch('/api/admin/alerts?limit=10'),
        ]);
        if (!mounted) return;
        setStats(s);
        setAlerts(a.alerts);
      } catch (e) {
        if (!mounted) return;
        setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  async function dismiss(id) {
    try {
      const token = await auth.currentUser.getIdToken();
      await fetch(`/api/admin/alerts/${id}/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      alert('Dismiss failed: ' + e.message);
    }
  }

  const page = { fontFamily: 'sans-serif', padding: '28px 32px', maxWidth: 1100, margin: '0 auto', color: '#1e293b' };

  return (
    <Layout>
    <div style={page}>
      <AdminLoadingStyles />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>Admin Panel</span>
          <AdminSpinner loading={loading} />
        </h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <Link to="/admin/intelligence" style={{ textDecoration: 'none', background: '#1d4ed8', color: '#fff', padding: '8px 16px', borderRadius: 6, fontSize: 14 }}>Admin Intelligence</Link>
          <Link to="/admin/gem-cleaned-contracts" style={{ textDecoration: 'none', background: '#0f766e', color: '#fff', padding: '8px 16px', borderRadius: 6, fontSize: 14 }}>GeM Contracts Full Dashboard</Link>
          <Link to="/admin/cpwd-tender-results" style={{ textDecoration: 'none', background: '#7c2d12', color: '#fff', padding: '8px 16px', borderRadius: 6, fontSize: 14 }}>CPWD Results</Link>
          <Link to="/admin/alerts" style={{ textDecoration: 'none', background: '#3b82f6', color: '#fff', padding: '8px 16px', borderRadius: 6, fontSize: 14 }}>View All Alerts</Link>
          <Link to="/admin/users"  style={{ textDecoration: 'none', background: '#0f172a', color: '#fff', padding: '8px 16px', borderRadius: 6, fontSize: 14 }}>Manage Users</Link>
        </div>
      </div>
      <AdminLoadingBar loading={loading} />

      {error && <div style={{ color: '#dc2626', marginBottom: 16 }}>Error: {error}</div>}

      {stats && (
        <div style={row}>
          <div style={card}><div style={label}>Active Plans</div><div style={big}>{stats.activeSubs}</div></div>
          <div style={card}><div style={label}>Expired Plans</div><div style={big}>{stats.expiredSubs}</div></div>
          <div style={{ ...card, borderColor: stats.pendingAlerts > 0 ? '#f87171' : '#e2e8f0' }}>
            <div style={label}>Pending Alerts</div>
            <div style={{ ...big, color: stats.pendingAlerts > 0 ? '#dc2626' : '#1e293b' }}>{stats.pendingAlerts}</div>
          </div>
          <div style={{ ...card, borderColor: stats.blockedUsers > 0 ? '#fb923c' : '#e2e8f0' }}>
            <div style={label}>Blocked Users</div>
            <div style={{ ...big, color: stats.blockedUsers > 0 ? '#ea580c' : '#1e293b' }}>{stats.blockedUsers}</div>
          </div>
          {Object.entries(stats.planCounts || {}).map(([plan, count]) => (
            <div key={plan} style={card}>
              <div style={label}>{plan} plan</div>
              <div style={big}>{count}</div>
            </div>
          ))}
        </div>
      )}

      <h2 style={{ fontSize: 17, marginBottom: 12 }}>Recent Pending Alerts</h2>
      {!loading && alerts.length === 0 ? (
        <p style={{ color: '#64748b' }}>No pending alerts.</p>
      ) : null}
      {!loading && alerts.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              {['Type', 'Phone', 'IP', 'Detail', 'Time', 'Action'].map((h) => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {alerts.map((a) => (
              <tr key={a.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#dc2626' }}>{a.type}</td>
                <td style={{ padding: '8px 12px' }}>
                  {a.phone ? <Link to={`/admin/users/${encodeURIComponent(a.phone)}`} style={{ color: '#3b82f6' }}>{a.phone}</Link> : '—'}
                </td>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{a.ip || '—'}</td>
                <td style={{ padding: '8px 12px' }}>
                  {a.type === 'HIGH_REQUEST_RATE' && `${a.requestCount} reqs / ${a.windowMin} min`}
                  {a.type === 'MULTI_USER_IP'     && `${a.uniqueUserCount} users from IP`}
                </td>
                <td style={{ padding: '8px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                  {a.createdAt ? new Date(a.createdAt).toLocaleString() : '—'}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <button onClick={() => dismiss(a.id)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', background: '#fff' }}>
                    Dismiss
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
    </Layout>
  );
}
